// src/components/(dashboard)/login-register/NameInput.tsx
import React, { useRef, useState } from 'react';
import { FaUser } from 'react-icons/fa';
import { MdEmail } from 'react-icons/md';
import { Input } from '@/components/ui/input';

interface NameInputProps {
    value: string;
    onChange: (val: string) => void;
    onSubmit: (val: string) => void;
    loading: boolean;
    textBtn?: string;
    newStyle?: boolean;
    email?: string;
    setEmail?: (email: string) => void;
    popupWizardStyle?: boolean;
    children?: React.ReactNode;
}

const NameInput: React.FC<NameInputProps> = ({
    value,
    onChange,
    onSubmit,
    loading,
    textBtn,
    newStyle,
    email,
    setEmail,
    popupWizardStyle,
    children,
}) => {
    const [step, setStep] = useState<1 | 2>(1); // 👈 new: local 2-step flow
    const nameRef = useRef<HTMLInputElement | null>(null);
    const emailRef = useRef<HTMLInputElement | null>(null);

    const goNextOrSubmit = () => {
        if (step === 1) {
            if (!value.trim()) {
                nameRef.current?.focus();
                return;
            }
            setStep(2); // 👈 reveal the email field
            // slight delay then focus email
            requestAnimationFrame(() => emailRef.current?.focus());
            return;
        }

        // step 2 → submit (only if email is present)
        if (!value.trim()) {
            setStep(1);
            nameRef.current?.focus();
            return;
        }
        if (!email?.trim()) {
            emailRef.current?.focus();
            return;
        }
        onSubmit(value.trim());
    };

    const canClick =
        step === 1 ? !!value.trim() && !loading : !!value.trim() && !!email?.trim() && !loading;

    return (
        <div
            className={`flex flex-col items-start justify-center w-full ${newStyle || popupWizardStyle ? 'mt-4 md:mt-6 gap-3 md:gap-4' : 'gap-2 '}`}
        >
            {/* Name input (always visible) */}
            <div
                className={`${newStyle || popupWizardStyle ? '' : 'overflow-hidden'} input-field border group border-secondary/10 w-full relative rounded-xl ${
                    popupWizardStyle
                        ? 'border-gray-300 dark:border-white/15 bg-white dark:bg-[#1a1a1a] shadow-2xs'
                        : newStyle
                          ? 'border-gray-300 dark:border-white/20'
                          : 'bg-gray-100'
                } transition-all duration-300`}
            >
                {!newStyle && !popupWizardStyle && (
                    <FaUser
                        className='absolute top-1/2 transform -translate-y-1/2 right-3'
                        color='#2d2c2c'
                        size={18}
                    />
                )}

                <Input
                    ref={nameRef}
                    className={`pl-5 !bg-transparent border-none py-4 md:py-6 ${
                        popupWizardStyle
                            ? 'text-gray-900 dark:text-white px-4'
                            : newStyle
                              ? 'text-gray-900 dark:text-white px-5'
                              : 'ps-9 px-2 text-black'
                    } z-10 focus-visible:outline-0 focus-visible:!ring-offset-0 shadow-none !text-base relative w-full`}
                    type='text'
                    required
                    value={value}
                    id='fullname'
                    onChange={(e) => onChange(e.target.value)}
                    disabled={loading}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            if (!loading) goNextOrSubmit();
                        }
                    }}
                />

                <label
                    htmlFor='fullname'
                    className={`absolute ${
                        popupWizardStyle
                            ? 'top-1/2 right-4 text-gray-400 dark:text-white/40'
                            : newStyle
                              ? 'top-0 text-gray-500 dark:text-white/50 !bg-primary-active z-[2] px-2 right-3'
                              : 'top-1/2 right-10 text-gray-500 dark:text-black/40'
                    } transition-all duration-200 -translate-y-1/2 text-sm ${value ? 'hidden' : ''}`}
                >
                    نام و نام خانوادگی
                </label>
            </div>

            {/* Email input (step 2 with simple slide/fade-in animation) */}
            <div
                className={`${newStyle || popupWizardStyle ? '' : 'overflow-hidden'} w-full transition-all duration-300 ${
                    step === 2
                        ? 'max-h-[500px] opacity-100 translate-y-0'
                        : 'max-h-0 opacity-0 -translate-y-2'
                }`}
            >
                <div
                    className={`input-field border group border-secondary/10 w-full relative rounded-xl ${
                        popupWizardStyle
                            ? 'border-gray-300 dark:border-white/15 bg-white dark:bg-[#1a1a1a] shadow-2xs'
                            : newStyle
                              ? 'border-gray-300 dark:border-white/20'
                              : 'bg-gray-100'
                    }`}
                >
                    {!newStyle && !popupWizardStyle && (
                        <MdEmail
                            className='absolute top-1/2 transform -translate-y-1/2 right-3'
                            color='#2d2c2c'
                            size={18}
                        />
                    )}

                    <Input
                        ref={emailRef}
                        dir='ltr'
                        className={`!bg-transparent border-none py-4 md:py-6 ${
                            popupWizardStyle
                                ? 'text-gray-900 dark:text-white px-4'
                                : newStyle
                                  ? 'text-gray-900 dark:text-white px-5'
                                  : 'ps-5 px-2 text-black'
                        } z-10 focus-visible:outline-0 focus-visible:!ring-offset-0 shadow-none !text-base relative w-full`}
                        type='email'
                        required={step === 2}
                        value={email}
                        id='Email'
                        onChange={(e) => setEmail?.(e.target.value)}
                        disabled={loading}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                if (!loading) goNextOrSubmit();
                            }
                        }}
                    />

                    <label
                        htmlFor='Email'
                        className={`absolute ${
                            popupWizardStyle
                                ? 'top-1/2 right-4 text-gray-400 dark:text-white/40'
                                : newStyle
                                  ? 'top-0 text-gray-500 dark:text-white/50 !bg-primary-active z-[2] px-2 right-3'
                                  : 'top-1/2 right-10 text-gray-500 dark:text-black/40'
                        } transition-all duration-200 -translate-y-1/2 text-sm ${email ? 'hidden' : ''}`}
                    >
                        ایمیل
                    </label>
                </div>

                {children}
            </div>

            {/* Button */}
            <button
                onClick={goNextOrSubmit}
                className={`py-3.5 md:py-4 px-4 ${newStyle ? 'bg-[#EEAC43]' : popupWizardStyle ? 'bg-gradient-to-r from-[#e7c282] to-[#b48336] text-black' : 'bg-progress'} w-full text-white rounded-xl ${
                    !canClick ? 'opacity-40' : ''
                }`}
                disabled={!canClick}
            >
                {loading ? 'در حال ارسال ...' : step === 1 ? 'ادامه' : (textBtn ?? 'ادامه')}
            </button>
        </div>
    );
};

export default NameInput;
